import React, { useState, useMemo } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumbs, Button } from '../../components/shared';
import { useNavigate } from 'react-router-dom';
import ExchangeRateTable from './ExchangeRateTable';
import SearchBar from '../../components/SearchBar/SearchBar';
import NewFilterDrawer from '../../components/shared/FilterDrawer/NewFilterDrawer';
import FILTER_CONSTANTS, { FILTER_VALUES } from '../../constants/FilterConstants';
import BREADCRUMB_NAMES from '../../constants/breadcrumbNames';
import { routeConstants } from '../../constants/routeConstants';
import { DEFAULT_PAGE } from '../../constants/pagination';
import { setExchangeRatesFilters, resetExchangeRatesState } from '../../redux-slice/exchangeRatesFilters';
import { useLazyGetCurrenciesQuery } from '../../services/Currencies/CurrenciesService';
import { useLazyGetUsersForSelectQuery } from '../../services/Users/UsersService';
import { CURRENCY_CONSTANT } from '../../constants/CurrencyConstants';

const CurrencyRatesExchange = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [total, setTotal] = useState(0);
  const [getCurrencies] = useLazyGetCurrenciesQuery();
  const [getUsersForSelect] = useLazyGetUsersForSelectQuery();

  const { exchangeRates } = useSelector((state) => state.exchangeRatesFilters || {});
  const tabFilters = exchangeRates || {
    [FILTER_VALUES.CURRENCY_FILTER]: [],
    [FILTER_VALUES.PROVIDER_FILTER]: null,
    [FILTER_VALUES.DATE_RANGE]: { from: '', to: '' },
    [FILTER_VALUES.UPDATED_BY_FILTER]: [],
    searchText: '',
    pageNumber: DEFAULT_PAGE,
  };

  const filterParams = useMemo(
    () => ({
      [FILTER_VALUES.CURRENCY_FILTER]: tabFilters[FILTER_VALUES.CURRENCY_FILTER] ?? [],
      [FILTER_VALUES.PROVIDER_FILTER]: tabFilters[FILTER_VALUES.PROVIDER_FILTER] ?? null,
      [FILTER_VALUES.DATE_RANGE]: tabFilters[FILTER_VALUES.DATE_RANGE] ?? { from: '', to: '' },
      [FILTER_VALUES.UPDATED_BY_FILTER]: tabFilters[FILTER_VALUES.UPDATED_BY_FILTER] ?? [],
    }),
    [tabFilters],
  );

  const getCurrencyAPI = () =>
    getCurrencies({ includeInactive: false })
      .then((response) => {
        if (!response?.isSuccess) return response;
        const activeCurrencies = (response.data || []).filter(
          (currency) => currency?.isActive === true && currency?.abbreviation !== CURRENCY_CONSTANT.USD,
        );
        return {
          ...response,
          data: activeCurrencies,
        };
      })
      .catch((error) => ({
        isSuccess: false,
        error,
      }));

  const filterData = useMemo(
    () =>
      FILTER_CONSTANTS.FILTER_DATA.EXCHANGE_RATES.FILTER.map((item) => {
        if (item.value === FILTER_VALUES.CURRENCY_FILTER) return { ...item, api: getCurrencyAPI };
        if (item.value === FILTER_VALUES.UPDATED_BY_FILTER) return { ...item, api: getUsersForSelect };
        return item;
      }),
    [getCurrencies, getUsersForSelect],
  );

  const {
    SETTINGS: { CURRENCY_RATES, SETTINGS },
  } = BREADCRUMB_NAMES;

  const breadcrumbData = useMemo(
    () => [{ text: SETTINGS, redirection: routeConstants.SETTING_ROUTE }, { text: CURRENCY_RATES }],
    [],
  );

  const { DRAWER_TYPES, TITLE } = FILTER_CONSTANTS;

  const onApplyFilterHandler = (appliedFilters) => {
    // Clear fetchedTimeframeRange when the filter drawer is explicitly applied so the
    // filter drawer's date range takes exclusive control of the table date filter.
    dispatch(
      setExchangeRatesFilters({
        ...tabFilters,
        ...appliedFilters,
        [FILTER_VALUES.FETCHED_TIMEFRAME_RANGE]: { from: '', to: '' },
        pageNumber: DEFAULT_PAGE,
      }),
    );
  };

  const resetFilterParams = () => {
    dispatch(resetExchangeRatesState());
  };

  const searchHandler = (params) => {
    dispatch(setExchangeRatesFilters({ ...tabFilters, searchText: params, pageNumber: DEFAULT_PAGE }));
  };

  const onSearchReset = () => {
    dispatch(setExchangeRatesFilters({ ...tabFilters, searchText: '', pageNumber: DEFAULT_PAGE }));
  };

  const onOpenAddUpdatePage = () => {
    navigate(
      `/${routeConstants.SETTING_ROUTE}/${routeConstants.CURRENCY_RATES_EXCHANGE_ROUTE}/${routeConstants.ADD_UPDATE_CURRENCY_RATE_ROUTE}`,
    );
  };

  const onBackToMainTable = () => {
    dispatch(resetExchangeRatesState());
  };

  // Show BACK button only when data is fetched from manual trigger (FETCHED_TIMEFRAME_RANGE has values)
  const isFetchedFromManualTrigger =
    tabFilters[FILTER_VALUES.FETCHED_TIMEFRAME_RANGE]?.from && tabFilters[FILTER_VALUES.FETCHED_TIMEFRAME_RANGE]?.to;
  return (
    <React.Fragment>
      <Breadcrumbs variant="body2" underline="hover" data={breadcrumbData} />
      <Typography mt="34px" variant="h1">
        Exchange Rates
      </Typography>
      <Typography variant="h3" style={{ color: theme.palette.secondary.main }}>{`${
        total > 0 ? `${total} Exchange Rates` : ''
      }`}</Typography>

      {/* Search and Filter Bar */}
      <Box display="flex" marginBottom={1} padding={2} gap={2} alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SearchBar
            onSearch={searchHandler}
            placeholder="Search by currency code or name"
            onReset={onSearchReset}
            searchText={tabFilters.searchText || ''}
          />
        </Box>
        <Box sx={{ pl: 1, flexShrink: 0 }}>
          <NewFilterDrawer
            filteredValues={filterParams}
            name={TITLE?.FILTERS?.toUpperCase() || 'FILTERS'}
            title={TITLE?.FILTERS || 'Filters'}
            drawerType={DRAWER_TYPES?.FILTER_DRAWER}
            onReset={resetFilterParams}
            onApplyFilter={onApplyFilterHandler}
            filterData={filterData}
            emptyFilterLength={JSON.stringify(FILTER_CONSTANTS.FILTER_DATA.EXCHANGE_RATES.INITIAL_PARAMS || {}).length}
          />
        </Box>
        <Button variant="contained" onClick={onOpenAddUpdatePage} sx={{ minWidth: 130, flexShrink: 0 }}>
          MANUAL TRIGGER
        </Button>
        {isFetchedFromManualTrigger && (
          <Button onClick={onBackToMainTable} sx={{ minWidth: 110, flexShrink: 0 }}>
            BACK
          </Button>
        )}
      </Box>

      <Box component={Paper}>
        <ExchangeRateTable onTotalChange={(value) => setTotal(value)} />
      </Box>
    </React.Fragment>
  );
};

export default CurrencyRatesExchange;
